import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Form } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';

import { Link } from 'react-router-dom';

import { toast } from 'react-toastify';

import { confirmToast } from '../utils/toast';

import CustomNavbar from '../components/CustomNavbar';

/* One place where a ticket status becomes a colour and an icon. Everything
   tinted on a card — top strip, pill, avatar, hover glow — reads from here. */
const TICKET_STATES = {
  Open: { icon: '🔔', from: '#991b1b', to: '#f87171', solid: '#ef4444' },
  'In Progress': {
    icon: '⚙️',
    from: '#b45309',
    to: '#fcd34d',
    solid: '#f59e0b'
  },
  Closed: { icon: '✅', from: '#15803d', to: '#4ade80', solid: '#22c55e' }
};

const stateOf = (status) => TICKET_STATES[status] || TICKET_STATES.Open;

const DAY = 1000 * 60 * 60 * 24;

/* How long an unresolved ticket has been waiting. Ages into amber then red,
   so a queue that is slipping is visible without reading a single date. */
const ageToneOf = (days) => {
  if (days >= 7) return 'is-hot';
  if (days >= 3) return 'is-warm';

  return 'is-cool';
};

function AdminTicketsPage() {
  const [tickets, setTickets] = useState([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const { data } = await axios.get(`${API}/tickets`);

        setTickets(data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchTickets();
  }, []);

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.name?.toLowerCase().includes(search.toLowerCase()) ||
      ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
      ticket.email?.toLowerCase().includes(search.toLowerCase()) ||
      ticket.productName?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === 'All' || ticket.status === statusFilter;

    return matchesSearch && matchesStatus;
  });
  const deleteTicketHandler = async (id) => {
    const confirmDelete = await confirmToast(
      'Are you sure you want to delete this ticket?'
    );

    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/tickets/${id}`);

      setTickets(tickets.filter((ticket) => ticket._id !== id));

      toast.success('Ticket Deleted Successfully');
    } catch (error) {
      console.log(error);

      toast.error('Failed To Delete Ticket');
    }
  };

  const countOf = (status) =>
    status === 'All'
      ? tickets.length
      : tickets.filter((t) => t.status === status).length;

  const filters = ['All', 'Open', 'In Progress', 'Closed'];

  return (
    <>
      <CustomNavbar />

      <div className="atk-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 atk-hero">
            <span className="atk-hero-aurora" aria-hidden="true" />
            <span className="atk-hero-blob atk-hero-blob-1" />
            <span className="atk-hero-blob atk-hero-blob-2" />

            {/* Motes drifting up through the banner */}
            <span className="atk-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="atk-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4 p-md-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h1 className="fw-bold mb-1 atk-hero-title">
                  <span className="atk-hero-icon">🎫</span>
                  <span className="atk-hero-title-text">Support Tickets</span>
                </h1>
                <p className="mb-0 atk-hero-text">
                  Manage and resolve customer support requests
                </p>
              </div>

              <div className="atk-stats">
                <div className="atk-stat">
                  <span className="atk-stat-value">{tickets.length}</span>
                  <span className="atk-stat-label">Total</span>
                </div>

                <div className="atk-stat is-open">
                  <span className="atk-stat-value">{countOf('Open')}</span>
                  <span className="atk-stat-label">Open</span>
                </div>

                <div className="atk-stat is-progress">
                  <span className="atk-stat-value">
                    {countOf('In Progress')}
                  </span>
                  <span className="atk-stat-label">Working</span>
                </div>

                <div className="atk-stat is-closed">
                  <span className="atk-stat-value">{countOf('Closed')}</span>
                  <span className="atk-stat-label">Closed</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Search */}
          <div className="atk-search-row">
            <div className="atk-search">
              <span className="atk-search-icon" aria-hidden="true">
                🔍
              </span>

              <Form.Control
                className="atk-search-input"
                placeholder="Search by name, email, subject or product..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  type="button"
                  className="atk-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  ×
                </button>
              )}
            </div>

            <span className="atk-result-count">
              <span className="atk-result-num">{filteredTickets.length}</span>
              shown
            </span>
          </div>

          {/* Status filters */}
          <div className="atk-chips">
            {filters.map((key) => {
              const meta = key === 'All' ? null : stateOf(key);

              return (
                <button
                  key={key}
                  type="button"
                  className={`atk-chip${statusFilter === key ? ' is-active' : ''}`}
                  style={{ '--chip': meta ? meta.solid : '#9a3412' }}
                  onClick={() => setStatusFilter(key)}
                >
                  {meta ? `${meta.icon} ${key}` : '✨ All'}
                  <span className="atk-chip-count">{countOf(key)}</span>
                </button>
              );
            })}
          </div>

          {/* Ticket cards */}
          {filteredTickets.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '18px' }}
            >
              <Card.Body>
                <div className="atk-empty-icon">🎫</div>
                <h5 className="fw-bold mt-2">No Tickets Found</h5>
                <p className="text-muted mb-0">
                  Try a different search or filter.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredTickets.map((ticket, index) => {
                const state = stateOf(ticket.status);

                /* How long this ticket has been waiting — only meaningful
                   while it is still unresolved. */
                const ageDays = Math.floor(
                  (Date.now() - new Date(ticket.createdAt).getTime()) / DAY
                );
                const showAge = ticket.status !== 'Closed';

                // Days left before a closed ticket is auto-deleted (7 days after closedAt)
                let daysLeft = null;
                if (ticket.status === 'Closed' && ticket.closedAt) {
                  const elapsedDays =
                    (Date.now() - new Date(ticket.closedAt).getTime()) / DAY;
                  daysLeft = Math.max(0, Math.ceil(7 - elapsedDays));
                }

                return (
                  <Col xl={4} lg={6} md={6} xs={12} key={ticket._id}>
                    <Card
                      className="border-0 shadow-sm h-100 atk-card"
                      style={{
                        '--t-from': state.from,
                        '--t-to': state.to,
                        '--t-accent': state.solid,
                        /* capped so late cards don't wait seconds */
                        '--i': Math.min(index, 11)
                      }}
                    >
                      <span className="atk-card-strip" aria-hidden="true" />

                      <Card.Body className="d-flex flex-column p-4">
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                          <span className="atk-id">
                            #{ticket._id.slice(-6)}
                          </span>

                          <span className="atk-status-pill">
                            <span className="atk-status-dot" aria-hidden="true" />
                            {state.icon} {ticket.status}
                          </span>
                        </div>

                        <h5 className="fw-bold atk-subject mb-2">
                          {ticket.subject}
                        </h5>

                        <div className="atk-meta mb-3">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="atk-avatar">
                              {ticket.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                            <div className="text-truncate">
                              <div className="fw-semibold text-truncate">
                                {ticket.name}
                              </div>
                              <small className="text-muted text-truncate d-block">
                                {ticket.email}
                              </small>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span className="atk-tag">🏷️ {ticket.category}</span>
                          <span className="atk-tag">
                            📦 {ticket.productName || 'N/A'}
                          </span>
                        </div>

                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <small className="text-muted">
                            📅 {new Date(ticket.createdAt).toLocaleDateString()}
                          </small>

                          {showAge && (
                            <span className={`atk-age ${ageToneOf(ageDays)}`}>
                              ⏱{' '}
                              {ageDays === 0
                                ? 'Today'
                                : `${ageDays}d waiting`}
                            </span>
                          )}
                        </div>

                        {daysLeft !== null && (
                          <div
                            className={`atk-autodelete mb-3${
                              daysLeft <= 1 ? ' is-urgent' : ''
                            }`}
                          >
                            🗑{' '}
                            {daysLeft === 0
                              ? 'Auto-deletes today'
                              : `Auto-deletes in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`}
                          </div>
                        )}

                        <div className="d-flex gap-2 mt-auto">
                          <Link
                            to={`/admin/tickets/${ticket._id}`}
                            className="atk-act atk-act-view flex-fill"
                          >
                            <FaEye className="atk-act-ico" /> View
                          </Link>

                          <button
                            type="button"
                            className="atk-act atk-act-del flex-fill"
                            onClick={() => deleteTicketHandler(ticket._id)}
                          >
                            <span className="atk-act-ico" aria-hidden="true">
                              🗑
                            </span>
                            Delete
                          </button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{ticketsStyles}</style>
    </>
  );
}

const ticketsStyles = `
  .atk-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 48px) 0;
  }

  /* ===== Sanctum banner ===== */
  .atk-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 22px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 30%, #7c2d12 64%, #ea580c);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: atkHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes atkHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  .atk-hero::before,
  .atk-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: atkThread 3.6s linear infinite;
  }
  .atk-hero::before { top: 0; }
  .atk-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes atkThread {
    to { background-position: 220% 0; }
  }

  .atk-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.40), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.38), transparent 62%);
    filter: blur(30px);
    animation: atkAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes atkAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .atk-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
    z-index: 0;
  }
  .atk-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 20%;
  }
  .atk-hero-blob-2 {
    width: 180px; height: 180px;
    background: #38bdf8;
    bottom: -90px; right: 8%;
    opacity: 0.3;
  }

  .atk-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .atk-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: atkMote 12s linear infinite;
  }
  .atk-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .atk-mote:nth-child(2) { left: 20%; animation-delay: 4.3s; width: 4px; height: 4px; }
  .atk-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .atk-mote:nth-child(4) { left: 46%; animation-delay: 7.2s; width: 5px; height: 5px; }
  .atk-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .atk-mote:nth-child(6) { left: 72%; animation-delay: 9.2s; width: 4px; height: 4px; }
  .atk-mote:nth-child(7) { left: 85%; animation-delay: 5.6s; }
  .atk-mote:nth-child(8) { left: 95%; animation-delay: 3.4s; width: 5px; height: 5px; }
  @keyframes atkMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-230px) translateX(22px) scale(1.1); }
  }

  .atk-hero .card-body { z-index: 2; }
  .atk-hero-title { display: flex; align-items: center; gap: 10px; }
  .atk-hero-icon {
    display: inline-block;
    animation: atkIconBob 3.2s ease-in-out infinite;
  }
  @keyframes atkIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-8deg); }
  }
  .atk-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: atkTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes atkTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .atk-hero-text { opacity: 0.85; }

  .atk-stats { display: flex; flex-wrap: wrap; gap: 9px; }
  .atk-stat {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    min-width: 78px;
    padding: 9px 15px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(253, 230, 138, 0.3);
    backdrop-filter: blur(6px);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, border-color 0.25s ease;
  }
  .atk-stat:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(253, 230, 138, 0.65);
  }
  .atk-stat-value { font-size: 1.3rem; font-weight: 800; line-height: 1.1; }
  .atk-stat-label {
    font-size: 0.63rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.78;
    margin-top: 2px;
  }
  .atk-stat.is-open .atk-stat-value     { color: #fca5a5; }
  .atk-stat.is-progress .atk-stat-value { color: #fcd34d; }
  .atk-stat.is-closed .atk-stat-value   { color: #6ee7b7; }
  /* An open queue is the one that needs attention */
  .atk-stat.is-open { animation: atkAlert 2.4s ease-in-out infinite; }
  @keyframes atkAlert {
    0%, 100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.45); }
    50%      { box-shadow: 0 0 0 7px rgba(248, 113, 113, 0); }
  }

  /* ===== Search ===== */
  .atk-search-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }
  .atk-search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 320px;
    max-width: 520px;
    padding: 4px 14px;
    border-radius: 50px;
    background: #fff;
    border: 2px solid rgba(245, 158, 11, 0.26);
    box-shadow: 0 10px 28px rgba(194, 65, 12, 0.1);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .atk-search:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 14px 36px rgba(245, 158, 11, 0.28);
  }
  .atk-search-icon { flex: none; font-size: 1rem; }
  .atk-search-input {
    border: none !important;
    padding: 9px 0;
    box-shadow: none !important;
    background: transparent;
  }
  .atk-search-clear {
    flex: none;
    border: none;
    width: 22px; height: 22px;
    border-radius: 50%;
    line-height: 1;
    font-size: 1.05rem;
    color: #b45309;
    background: #fff7ed;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .atk-search-clear:hover { background: #fde68a; transform: rotate(90deg); }
  .atk-result-count {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #9a3412;
  }
  .atk-result-num {
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    border-radius: 30px;
    padding: 2px 11px;
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }

  /* ===== Status chips ===== */
  .atk-chips { display: flex; flex-wrap: wrap; gap: 9px; margin-bottom: 22px; }
  .atk-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 9px 7px 15px;
    border-radius: 50px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid var(--chip, #f59e0b);
    color: var(--chip, #9a3412);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
  }
  .atk-chip:hover { transform: translateY(-2px); }
  .atk-chip:active { transform: translateY(0) scale(0.96); }
  .atk-chip.is-active {
    color: #fff;
    background: var(--chip, #ea580c);
    animation: atkChipPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes atkChipPop {
    0%   { transform: scale(0.88); }
    55%  { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
  .atk-chip-count {
    min-width: 22px;
    padding: 1px 8px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 800;
    background: #fff7ed;
    color: var(--chip, #9a3412);
  }
  .atk-chip.is-active .atk-chip-count { background: rgba(255, 255, 255, 0.92); }

  /* ===== Ticket cards ===== */
  .atk-card {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(245, 158, 11, 0.14) !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: atkCardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 60ms);
  }
  @keyframes atkCardIn {
    from { opacity: 0; transform: translateY(20px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  /* The old 5px inline border, but alive */
  .atk-card-strip {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: linear-gradient(90deg,
      var(--t-from, #ea580c), var(--t-to, #f59e0b), var(--t-from, #ea580c));
    background-size: 220% 100%;
    animation: atkStrip 3.4s linear infinite;
  }
  @keyframes atkStrip {
    to { background-position: 220% 0; }
  }
  .atk-card:hover {
    transform: translateY(-7px);
    box-shadow: 0 24px 42px color-mix(in srgb, var(--t-accent, #ea580c) 28%, transparent) !important;
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .atk-card:hover { box-shadow: 0 22px 40px rgba(194, 65, 12, 0.18) !important; }
  }

  .atk-id {
    font-weight: 800;
    color: #94a3b8;
    font-size: 0.85rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }

  .atk-status-pill {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 12px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;
    color: #fff;
    background: var(--t-accent, #ea580c);
  }
  .atk-status-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #fff;
    animation: atkDot 1.7s ease-in-out infinite;
  }
  @keyframes atkDot {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50%      { opacity: 1;   transform: scale(1.35); }
  }

  .atk-subject {
    font-size: 1.05rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.6em;
    transition: color 0.25s ease;
  }
  .atk-card:hover .atk-subject { color: var(--t-accent, #ea580c); }

  .atk-avatar {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-weight: 700;
    background: linear-gradient(135deg, var(--t-from, #ea580c), var(--t-to, #f59e0b));
    box-shadow: 0 5px 14px color-mix(in srgb, var(--t-accent, #ea580c) 42%, transparent);
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .atk-avatar { box-shadow: 0 5px 14px rgba(124, 45, 18, 0.28); }
  }
  .atk-card:hover .atk-avatar { transform: scale(1.09) rotate(-5deg); }

  .atk-tag {
    display: inline-block;
    border: 1px solid #f2e3cd;
    background: #fffdf7;
    color: #57534e;
    border-radius: 30px;
    font-size: 0.78rem;
    font-weight: 500;
    padding: 5px 12px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: background 0.25s ease, border-color 0.25s ease,
      transform 0.25s ease;
  }
  .atk-tag:hover {
    transform: translateY(-2px);
    background: #fff7ed;
    border-color: #fcd34d;
  }

  /* ===== Waiting time — warms as a ticket ages ===== */
  .atk-age {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 10px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 700;
    border: 1px solid transparent;
  }
  .atk-age.is-cool {
    color: #0e7490;
    background: #ecfeff;
    border-color: #a5f3fc;
  }
  .atk-age.is-warm {
    color: #b45309;
    background: #fffbeb;
    border-color: #fcd34d;
  }
  .atk-age.is-hot {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
    animation: atkAgeAlert 2s ease-in-out infinite;
  }
  @keyframes atkAgeAlert {
    0%, 100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.5); }
    50%      { box-shadow: 0 0 0 5px rgba(248, 113, 113, 0); }
  }

  .atk-autodelete {
    display: inline-block;
    align-self: flex-start;
    font-size: 0.78rem;
    font-weight: 600;
    color: #b45309;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 30px;
    padding: 5px 12px;
  }
  /* About to vanish — say so louder */
  .atk-autodelete.is-urgent {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
    animation: atkAgeAlert 1.8s ease-in-out infinite;
  }

  /* ===== Card actions ===== */
  .atk-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border-radius: 12px;
    padding: 8px 14px;
    font-size: 0.83rem;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    background: #fff;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease, filter 0.25s ease;
  }
  .atk-act:hover { transform: translateY(-2px); }
  .atk-act:active { transform: translateY(0) scale(0.96); }
  .atk-act-ico { display: inline-block; }

  .atk-act-view {
    border: none;
    color: #fff;
    background: linear-gradient(135deg, #4c1d95, #7c3aed 55%, #a855f7);
    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.35);
  }
  .atk-act-view:hover {
    color: #fff;
    filter: brightness(1.08);
    box-shadow: 0 12px 26px rgba(124, 58, 237, 0.5);
  }
  .atk-act-view:hover .atk-act-ico { transform: scale(1.22); }
  .atk-act-view .atk-act-ico {
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .atk-act-del {
    border: 1px solid #fecaca;
    color: #b91c1c;
  }
  .atk-act-del:hover {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.42);
  }
  .atk-act-del:hover .atk-act-ico {
    animation: atkShake 0.55s ease-in-out infinite;
  }
  @keyframes atkShake {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    30%      { transform: translateX(-2px) rotate(-14deg); }
    65%      { transform: translateX(2px) rotate(11deg); }
  }

  .atk-empty-icon {
    font-size: 3rem;
    animation: atkIconBob 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .atk-hero,
    .atk-hero::before,
    .atk-hero::after,
    .atk-hero-aurora,
    .atk-mote,
    .atk-hero-icon,
    .atk-hero-title-text,
    .atk-stat.is-open,
    .atk-chip.is-active,
    .atk-card,
    .atk-card-strip,
    .atk-status-dot,
    .atk-age.is-hot,
    .atk-autodelete.is-urgent,
    .atk-act-del:hover .atk-act-ico,
    .atk-empty-icon { animation: none !important; }
    .atk-motes { display: none; }
    .atk-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminTicketsPage;
